import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * WebRTC screen-share between a tutor and a linked tutee.
 *
 * The student is the PRESENTER (captures their screen, creates the offer);
 * the teacher is the VIEWER (requests, answers, renders the video). The
 * backend only relays SDP/ICE over the existing teacher/student WebSockets —
 * the media itself is peer-to-peer and never touches the server. Every relay
 * is gated server-side on an ACTIVE teacher_student_link.
 *
 * NB: STUN-only. This punches through most home/NAT setups but can fail on
 * symmetric NATs (some school networks). A TURN relay would close that gap —
 * see VITE note below. The viewer hook surfaces a clear error on timeout
 * rather than spinning forever.
 */

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
  // To add a TURN relay later: push { urls, username, credential } here,
  // sourced from import.meta.env (do NOT hardcode credentials).
};

function buildWsUrl(path: string, token: string): string | null {
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '';
  if (!apiBase) return null;
  return (
    apiBase.replace(/^http/, 'ws').replace(/\/$/, '') +
    path +
    `?token=${encodeURIComponent(token)}`
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Teacher side — VIEWER
// ─────────────────────────────────────────────────────────────────────────────

export type ViewerStatus =
  | 'idle'
  | 'requesting'
  | 'waiting'
  | 'connecting'
  | 'live'
  | 'denied'
  | 'peer_offline'
  | 'ended'
  | 'error';

export function useScreenShareViewer(
  teacherId: string | null,
  teacherName: string | null,
  studentId: string | null,
  token: string | null,
) {
  const [status, setStatus] = useState<ViewerStatus>('idle');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const pendingIce = useRef<RTCIceCandidateInit[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const send = useCallback((type: string, data: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type, data })); } catch { /* noop */ }
    }
  }, []);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    pendingIce.current = [];
    try { pcRef.current?.close(); } catch { /* noop */ }
    pcRef.current = null;
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
    setStream(null);
  }, []);

  const stop = useCallback(() => {
    if (studentId) send('screenshare_stop', { student_id: studentId });
    cleanup();
    setStatus('idle');
  }, [studentId, send, cleanup]);

  const handleOffer = useCallback(async (payload: RTCSessionDescriptionInit) => {
    if (!studentId) return;
    setStatus('connecting');
    const pc = new RTCPeerConnection(RTC_CONFIG);
    pcRef.current = pc;
    pc.ontrack = (e) => {
      setStream(e.streams[0] || new MediaStream([e.track]));
      setStatus('live');
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send('webrtc_signal', { student_id: studentId, kind: 'ice', payload: e.candidate.toJSON() });
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') setStatus('error');
    };
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(payload));
      for (const c of pendingIce.current) {
        try { await pc.addIceCandidate(c); } catch { /* noop */ }
      }
      pendingIce.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send('webrtc_signal', { student_id: studentId, kind: 'answer', payload: answer });
    } catch {
      setStatus('error');
    }
  }, [studentId, send]);

  const request = useCallback(() => {
    if (!teacherId || !studentId || !token) return;
    const url = buildWsUrl(`/ws/teacher/${encodeURIComponent(teacherId)}`, token);
    if (!url) { setStatus('error'); return; }
    cleanup();
    setStatus('requesting');
    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch { setStatus('error'); return; }
    wsRef.current = ws;

    // Overall timeout — if we never reach 'live' (e.g. STUN can't punch the
    // NAT), surface an error instead of spinning forever.
    timeoutRef.current = setTimeout(() => {
      setStatus((s) => (s === 'live' ? s : 'error'));
    }, 25_000);

    ws.onopen = () => {
      send('screenshare_request', { student_id: studentId, teacher_name: teacherName || 'המורה' });
      setStatus('waiting');
    };
    ws.onclose = () => {
      setStatus((s) => (s === 'live' || s === 'connecting' ? 'ended' : s));
    };
    ws.onerror = () => { /* onclose handles state */ };
    ws.onmessage = (ev) => {
      let msg: { type?: string; data?: Record<string, unknown> };
      try { msg = JSON.parse(ev.data); } catch { return; }
      const d = msg.data || {};
      if (msg.type === 'ping') { send('pong', {}); return; }
      if (msg.type === 'screenshare_response') {
        if (d.accepted) setStatus('connecting');
        else { cleanup(); setStatus('denied'); }
      } else if (msg.type === 'webrtc_signal') {
        if (d.kind === 'offer') {
          void handleOffer(d.payload as RTCSessionDescriptionInit);
        } else if (d.kind === 'ice') {
          const cand = d.payload as RTCIceCandidateInit;
          const pc = pcRef.current;
          if (pc && pc.remoteDescription) void pc.addIceCandidate(cand).catch(() => {});
          else pendingIce.current.push(cand);
        }
      } else if (msg.type === 'screenshare_stop') {
        cleanup();
        setStatus('ended');
      } else if (msg.type === 'signal_error') {
        cleanup();
        setStatus(d.reason === 'peer_offline' ? 'peer_offline' : 'error');
      }
      // Other teacher-channel events (student_activity, live_stats, …) are
      // intentionally ignored here — the live-feed hook handles those.
    };
  }, [teacherId, teacherName, studentId, token, cleanup, send, handleOffer]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { status, stream, request, stop };
}

// ─────────────────────────────────────────────────────────────────────────────
// Student side — PRESENTER
// ─────────────────────────────────────────────────────────────────────────────

export type PresenterStatus = 'idle' | 'prompt' | 'connecting' | 'sharing' | 'error';

export interface IncomingShareRequest {
  teacherId: string;
  teacherName: string;
}

export function useScreenSharePresenter(
  studentId: string | null,
  token: string | null,
  enabled: boolean,
) {
  const [status, setStatus] = useState<PresenterStatus>('idle');
  const [incomingRequest, setIncomingRequest] = useState<IncomingShareRequest | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const activeTeacherRef = useRef<string | null>(null);
  const pendingIce = useRef<RTCIceCandidateInit[]>([]);

  const send = useCallback((type: string, data: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type, data })); } catch { /* noop */ }
    }
  }, []);

  const stopSharing = useCallback((notify = true) => {
    const teacherId = activeTeacherRef.current;
    if (notify && teacherId) send('screenshare_stop', { teacher_id: teacherId });
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
    try { pcRef.current?.close(); } catch { /* noop */ }
    pcRef.current = null;
    pendingIce.current = [];
    activeTeacherRef.current = null;
    setStatus('idle');
    setIncomingRequest(null);
  }, [send]);

  const deny = useCallback(() => {
    if (incomingRequest) {
      send('screenshare_response', { teacher_id: incomingRequest.teacherId, accepted: false });
    }
    setIncomingRequest(null);
    setStatus('idle');
  }, [incomingRequest, send]);

  const accept = useCallback(async () => {
    const req = incomingRequest;
    if (!req) return;
    setStatus('connecting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch {
      // User cancelled the browser's screen picker, or permission denied.
      send('screenshare_response', { teacher_id: req.teacherId, accepted: false });
      setIncomingRequest(null);
      setStatus('idle');
      return;
    }
    streamRef.current = stream;
    activeTeacherRef.current = req.teacherId;
    setIncomingRequest(null);
    send('screenshare_response', { teacher_id: req.teacherId, accepted: true });

    const pc = new RTCPeerConnection(RTC_CONFIG);
    pcRef.current = pc;
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    // The browser's own "Stop sharing" control ends the track directly.
    stream.getVideoTracks().forEach((t) => { t.onended = () => stopSharing(true); });
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send('webrtc_signal', { teacher_id: req.teacherId, kind: 'ice', payload: e.candidate.toJSON() });
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') setStatus('error');
      else if (pc.connectionState === 'connected') setStatus('sharing');
    };
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send('webrtc_signal', { teacher_id: req.teacherId, kind: 'offer', payload: offer });
      setStatus('sharing');
    } catch {
      stopSharing(true);
      setStatus('error');
    }
  }, [incomingRequest, send, stopSharing]);

  // Persistent listening socket — while a student is logged in we keep a WS
  // open so an incoming "watch my screen" request can prompt them wherever
  // they are in the app (notably mid-practice).
  useEffect(() => {
    if (!enabled || !studentId || !token) return;
    const url = buildWsUrl(`/ws/student/${encodeURIComponent(studentId)}`, token);
    if (!url) return;
    let alive = true;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (!alive) return;
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { reconnectTimer = setTimeout(connect, 4000); return; }
      wsRef.current = ws;
      ws.onclose = () => { if (alive) reconnectTimer = setTimeout(connect, 4000); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      ws.onmessage = (ev) => {
        let msg: { type?: string; data?: Record<string, unknown> };
        try { msg = JSON.parse(ev.data); } catch { return; }
        const d = msg.data || {};
        if (msg.type === 'ping') { send('pong', {}); return; }
        if (msg.type === 'screenshare_request') {
          const teacherId = String(d.teacher_id || '');
          if (!teacherId) return;
          // Already sharing with someone — auto-decline a second watcher.
          if (activeTeacherRef.current) {
            send('screenshare_response', { teacher_id: teacherId, accepted: false });
            return;
          }
          setIncomingRequest({ teacherId, teacherName: String(d.teacher_name || 'המורה') });
          setStatus('prompt');
        } else if (msg.type === 'webrtc_signal') {
          const pc = pcRef.current;
          if (!pc) return;
          if (d.kind === 'answer') {
            void pc
              .setRemoteDescription(new RTCSessionDescription(d.payload as RTCSessionDescriptionInit))
              .then(async () => {
                for (const c of pendingIce.current) {
                  try { await pc.addIceCandidate(c); } catch { /* noop */ }
                }
                pendingIce.current = [];
              })
              .catch(() => {});
          } else if (d.kind === 'ice') {
            const cand = d.payload as RTCIceCandidateInit;
            if (pc.remoteDescription) void pc.addIceCandidate(cand).catch(() => {});
            else pendingIce.current.push(cand);
          }
        } else if (msg.type === 'screenshare_stop') {
          stopSharing(false);
        }
      };
    };
    connect();

    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [enabled, studentId, token, send, stopSharing]);

  return { status, incomingRequest, accept, deny, stopSharing };
}
